import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook to manage notification state with auto-hide functionality
 *
 * @param {number} autoHideDuration - Duration in milliseconds before auto-hiding (default: 5000)
 *
 * @returns {object} Notification state and handlers
 */
export const useNotification = (autoHideDuration = 5000) => {
  const [notification, setNotification] = useState({ type: '', message: '' });
  const timerRef = useRef(null);

  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer to auto-hide
    if (autoHideDuration > 0) {
      timerRef.current = setTimeout(() => {
        setNotification({ type: '', message: '' });
      }, autoHideDuration);
    }
  }, [autoHideDuration]);

  const showSuccess = useCallback((message) => {
    showNotification('success', message);
  }, [showNotification]);

  const showError = useCallback((message) => {
    showNotification('error', message);
  }, [showNotification]);

  const showWarning = useCallback((message) => {
    showNotification('warning', message);
  }, [showNotification]);

  const showInfo = useCallback((message) => {
    showNotification('info', message);
  }, [showNotification]);

  const clearNotification = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setNotification({ type: '', message: '' });
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    notification,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearNotification,
  };
};

export default useNotification;
